/* eslint-disable no-console */
import { FC, ReactElement, ReactNode, useRef, useState, useEffect, useMemo, useCallback } from 'react'
import { useSwipeable } from 'react-swipeable'
import { useDebouncedCallback } from 'use-debounce'

import './index.css'

const Carousel: FC = ({ children }): ReactElement => {
  const cardWidth = 350
  const elements = useMemo(() => (children as ReactNode[]) || [], [children])

  const trackRef = useRef<HTMLDivElement | null>(null)
  const windowWidth = window.innerWidth < 2400 ? window.innerWidth : 2400
  const visibleSlides = useCallback(() => Math.floor((windowWidth + 150) / cardWidth), [cardWidth, windowWidth])
  const scrollWidth = (remainder?: number) => (remainder ? cardWidth * remainder : cardWidth * visibleSlides())
  const currentPosition = useCallback(
    () => (trackRef && trackRef.current ? Number((trackRef.current as HTMLElement).style.left.replace(/[^\-0-9]/g, '')) : 0),
    [trackRef]
  )
  const initEnd = useCallback(() => elements.length <= visibleSlides(), [elements, visibleSlides])
  const [start, setStart] = useState(true)
  const [end, setEnd] = useState(false)
  const [resized, setResized] = useState(false)

  const slideToView = useCallback(
    (index: number) => {
      if (trackRef && trackRef.current) {
        const trackElem = trackRef.current as HTMLElement
        const totalItems = elements.length
        const remainder = totalItems - index
        if (!(remainder - visibleSlides() <= -1)) {
          setStart(!index)
          trackElem.style.left = -cardWidth * index + 'px'
        } else {
          setEnd(true)
        }
      }
    },
    [elements.length, visibleSlides]
  )

  useEffect(() => {
    setEnd(initEnd())
  }, [initEnd])

  useEffect(() => {
    if (elements.length <= visibleSlides()) {
      setStart(true)
      setEnd(true)
    }
  }, [elements, start, end, visibleSlides])

  useEffect(() => {
    if (resized) {
      if (elements.length <= visibleSlides() - 1) {
        slideToView(0)
        setEnd(true)
        setStart(true)
      } else if (currentPosition() < 0 && currentPosition() !== elements.length * cardWidth) {
        setEnd(false)
        setStart(false)
      } else {
        setEnd(false)
      }
      setResized(false)
    }
  }, [currentPosition, elements.length, resized, slideToView, visibleSlides])

  const resizeHandler = useDebouncedCallback(() => setResized(true), 250)
  useEffect(() => {
    window.addEventListener('resize', resizeHandler)
    return () => window.removeEventListener('resize', resizeHandler)
  }, [resizeHandler])

  const moveTrack = (direction: 'left' | 'right'): void => {
    if (trackRef && trackRef.current) {
      const totalItems = elements.length
      const trackElem = trackRef.current as HTMLElement
      const scroll = {
        right: (remainder?: number) =>
          remainder ? currentPosition() - scrollWidth(remainder) + 'px' : currentPosition() - scrollWidth() + 'px',
        left: currentPosition() + scrollWidth() > 0 ? 0 + 'px' : currentPosition() + scrollWidth() + 'px',
      }

      if (currentPosition() < 0 && direction === 'left') {
        setEnd(false)
        trackElem.style.left = scroll.left
        if (currentPosition() === 0) setStart(true)
      }
      if (currentPosition() - scrollWidth() > -cardWidth * totalItems && direction === 'right') {
        setStart(false)
        const remainder = totalItems - Math.abs((currentPosition() - scrollWidth()) / cardWidth)
        trackElem.style.left = remainder < visibleSlides() ? scroll.right(remainder) : scroll.right()
        if (remainder < visibleSlides() || currentPosition() === -cardWidth * (totalItems - 1)) setEnd(true)
      }
      if (currentPosition() - scrollWidth() <= -cardWidth * totalItems && direction === 'right') setEnd(true)
    }
  }
  const swipeHandlers = useSwipeable({
    onSwipedLeft: () => moveTrack('right'),
    onSwipedRight: () => moveTrack('left'),
  })
  return (
    <div className="slider-wrapper" {...swipeHandlers} style={{ touchAction: 'pan-y' }}>
      <div className="slider-arrows">
        {!start && (
          <button
            aria-label="Slide to the left"
            className="slider-arrow slider-arrow-left fade-in"
            onClick={() => {
              moveTrack('left')
            }}
            value="<"
          >
            <i className="feather-chevron-left"></i>
          </button>
        )}
        {!end && (
          <button
            aria-label="Slide to the right"
            className="slider-arrow slider-arrow-right fade-in"
            onClick={() => {
              moveTrack('right')
            }}
            value=">"
          >
            <i className="feather-chevron-right"></i>
          </button>
        )}
      </div>
      <div className="slider">
        <div ref={trackRef} className="slider-track" style={{ left: 0 }}>
          {elements.map((item, index) => (
            <div
              key={'slide-' + index}
              className="slider-slide"
              onFocus={(e) => {
                window.addEventListener(
                  'keyup',
                  (e) => {
                    if (e.key === 'Tab') slideToView(index)
                  },
                  { once: true }
                )
              }}
            >
              {item}
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}

export default Carousel
